import {
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatCheckbox, MatCheckboxDefaultOptions, MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { DataObject, IUser } from '@miq/shared-models';
import { NgStyle, NgClass, UpperCasePipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormsModule } from '@angular/forms';
import { PrimeTemplate } from 'primeng/api';
import { L10nTranslationModule, L10nTranslationService } from 'angular-l10n';
import { MatIcon } from '@angular/material/icon';
import { getSecureRandomNumber } from '@miq/utils';
import { DialogService } from '@miq/platform';
import { ConfirmDialogComponent, ConfirmData } from '../../dialogs/confirm-dialog/confirm-dialog.component';
/*
 * L2-R6: moved from `src/app/shared/components/contact-card/contact-card.component.ts`
 * (share-report in the reports remote is its only consumer). Every dependency was already in a
 * library — DialogService in @miq/platform, ConfirmDialogComponent here in @miq/platform-ui,
 * IUser/DataObject in @miq/shared-models. The old host path is a re-export shim.
 */
const colors = [
  '#00b2dd',
  '#ec008c',
  '#a23f97',
  '#f58220',
  '#ffdd00',
  '#69be2b',
  '#004b35',
  '#00a657',
  '#bfd857',
  '#00b5db',
  '#d0021b',
];
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-contact-card',
  templateUrl: './contact-card.component.html',
  styleUrl: './contact-card.component.scss',
  providers: [
    { provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions },
  ],
  host: {
    '[class.space-entity]': 'isSpaceEntityClass',
  },
  imports: [
    NgStyle,
    MatTooltip,
    NgClass,
    MultiSelectModule,
    FormsModule,
    PrimeTemplate,
    MatCheckbox,
    L10nTranslationModule,
    MatIcon,
    UpperCasePipe
],
})
export class ContactCardComponent implements OnDestroy {
  private readonly checkbox = viewChild(MatCheckbox);
  public user = input<IUser>();
  public companyId = input<number>();
  public selected = input<boolean>();
  public isOnMIQ = input<boolean>();
  public showPinIcon = input(false);
  isPinned = input(false);
  change = output<{ checked: boolean; user: IUser; phones?: DataObject[] }>();
  public isSpaceEntity = input(false);
  pinToggled = output<{ user: IUser; isPinned: boolean }>();
  get isSpaceEntityClass() {
    return this.isSpaceEntity();
  }
  public color: string;
  public isActiveUser = signal<boolean>(undefined);
  public selectedPhones: number[] = [];
  private readonly translationService = inject(L10nTranslationService);
  private readonly dialogService = inject(DialogService);
  
  constructor() {
    const idx = Math.floor(getSecureRandomNumber() * 10 + 1);
    this.color = colors[idx];
    effect(() => {
      const userVal = this.user() as any;
      if (userVal) {
        const statusFields = [userVal.ref_individual_status_desc, userVal.statusVal, userVal.employee_status_desc];
        this.isActiveUser.set(statusFields.includes('Active'));
        if (this.isSpaceEntity() && userVal.phone?.length) {
          this.selectedPhones = [userVal.phone[0].id];
        }
      }
    });
  }
  onClick() {
    this.checkbox().toggle();
    this.checkAndEmit();
  }
  onPhoneSelectionChange(event) {
    this.selectedPhones = event?.value || [];
    this.checkAndEmit();
  }
  checkAndEmit() {
    // Get complete phone objects for selected phone IDs
    const selectedPhoneObjects = this.user()?.phone?.filter(phone => this.selectedPhones.includes(phone.id)) || [];
    this.change.emit({
      checked: this.checkbox().checked,
      user: this.user(),
      phones: selectedPhoneObjects,
    });
  }
  onPinClick(event: MouseEvent, isPinned: boolean) {
    event.stopPropagation();
    this.pinToggled.emit({ user: this.user(), isPinned: !isPinned });
  }
  
  onShowGroupMembers(event: MouseEvent) {
    event.stopPropagation();
    
    if (!this.user()?.isDistributionGroup) {
      return;
    }    
    const groupUsers = this.user()?.distributionGroupUsers || [];
    const groupName = this.user()?.distributionGroupName || '';    
    const emailListWithNames = groupUsers.map(user => ({
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown',
      email: user.email
    }));
    
    const dialogData: ConfirmData = {
      message: null,
      title: 'Info!',
      header: groupName,
      emailListWithNames: emailListWithNames.length > 0 ? emailListWithNames : undefined,
      multipleMessages: emailListWithNames.length === 0 ? [`${groupName} ${this.translationService.translate('admin.usergroup.groupHasNoUsers')}`] : undefined,
      confirmText: 'OK',
      hideIcon: true
     };
    
    this.dialogService.openDialog(ConfirmDialogComponent, 
      dialogData, { 
        panelClass: ['alert-modals', 'contact-card-email-list-modal']
      }).subscribe();
  }
  
  ngOnDestroy() {
  }
}
